/**
 * Journey palette for purchase orders and shipments — ordered from draft to complete.
 */

/** @typedef {'soft'|'solid'|'forest'|'redSolid'} JourneyTone */

/** @typedef {{ family: string, tone: JourneyTone }} JourneyStatusTone */

export const JOURNEY_FAMILY_ORDER = Object.freeze([
  "grey",
  "slate",
  "violet",
  "amber",
  "orange",
  "indigo",
  "blue",
  "cyan",
  "teal",
  "green",
  "emerald",
  "forest",
  "rose",
  "red",
]);

/**
 * @const {Record<string, { text: string, bgSoft: string, bgSolid: string, dotSoft: string, dotSolid: string, forest?: { text: string, bg: string, dot: string } }>}
 */
export const JOURNEY_PALETTE = Object.freeze({
  grey: {
    text: "#374151",
    bgSoft: "#f3f4f6",
    bgSolid: "#e5e7eb",
    dotSoft: "#9ca3af",
    dotSolid: "#6b7280",
  },
  slate: {
    text: "#334155",
    bgSoft: "#f1f5f9",
    bgSolid: "#e2e8f0",
    dotSoft: "#94a3b8",
    dotSolid: "#64748b",
  },
  violet: {
    text: "#5b21b6",
    bgSoft: "#f5f3ff",
    bgSolid: "#ede9fe",
    dotSoft: "#a78bfa",
    dotSolid: "#8b5cf6",
  },
  amber: {
    text: "#92400e",
    bgSoft: "#fffbeb",
    bgSolid: "#fef3c7",
    dotSoft: "#fbbf24",
    dotSolid: "#d97706",
  },
  orange: {
    text: "#9a3412",
    bgSoft: "#fff7ed",
    bgSolid: "#ffedd5",
    dotSoft: "#fb923c",
    dotSolid: "#ea580c",
  },
  indigo: {
    text: "#3730a3",
    bgSoft: "#eef2ff",
    bgSolid: "#e0e7ff",
    dotSoft: "#818cf8",
    dotSolid: "#4f46e5",
  },
  blue: {
    text: "#1e40af",
    bgSoft: "#eff6ff",
    bgSolid: "#dbeafe",
    dotSoft: "#60a5fa",
    dotSolid: "#2563eb",
  },
  cyan: {
    text: "#155e75",
    bgSoft: "#ecfeff",
    bgSolid: "#cffafe",
    dotSoft: "#22d3ee",
    dotSolid: "#0891b2",
  },
  teal: {
    text: "#115e59",
    bgSoft: "#f0fdfa",
    bgSolid: "#ccfbf1",
    dotSoft: "#2dd4bf",
    dotSolid: "#0d9488",
  },
  green: {
    text: "#166534",
    bgSoft: "#f0fdf4",
    bgSolid: "#dcfce7",
    dotSoft: "#4ade80",
    dotSolid: "#16a34a",
  },
  emerald: {
    text: "#065f46",
    bgSoft: "#ecfdf5",
    bgSolid: "#d1fae5",
    dotSoft: "#34d399",
    dotSolid: "#059669",
  },
  forest: {
    text: "#ffffff",
    bgSoft: "#166534",
    bgSolid: "#166534",
    dotSoft: "#bbf7d0",
    dotSolid: "#bbf7d0",
  },
  rose: {
    text: "#9f1239",
    bgSoft: "#fff1f2",
    bgSolid: "#ffe4e6",
    dotSoft: "#fb7185",
    dotSolid: "#e11d48",
  },
  red: {
    text: "#b91c1c",
    bgSoft: "#fee2e2",
    bgSolid: "#fecaca",
    dotSoft: "#f87171",
    dotSolid: "#dc2626",
  },
});

/** @type {ReadonlyArray<readonly [string, string, JourneyTone]>} */
const JOURNEY_STATUS_ENTRIES = Object.freeze([
  // grey
  ["Draft", "grey", "soft"],
  ["Closed", "grey", "solid"],
  // slate
  ["Draft Sent", "slate", "soft"],
  ["Created", "slate", "solid"],
  // violet
  ["Sent", "violet", "soft"],
  ["Supplier Reviewing", "violet", "soft"],
  ["Changes Proposed", "violet", "soft"],
  ["Awaiting Buyer Response", "violet", "soft"],
  ["Accepted", "violet", "solid"],
  // amber
  ["Scheduled", "amber", "soft"],
  ["In Production", "amber", "soft"],
  ["Ready for Allocation", "amber", "solid"],
  // orange
  ["Not Allocated", "orange", "soft"],
  ["Partially Allocated", "orange", "soft"],
  ["Fully Allocated", "orange", "solid"],
  // indigo
  ["Seller Location", "indigo", "soft"],
  ["Booked", "indigo", "soft"],
  ["Partially Gate In at Loading Port", "indigo", "soft"],
  ["Cargo Ready", "indigo", "solid"],
  ["Gate In at Loading Port", "indigo", "solid"],
  // blue
  ["Loaded", "blue", "soft"],
  ["Partially Loaded at Loading Port", "blue", "soft"],
  ["Partially Departed Loading Port", "blue", "soft"],
  ["Partially Accepted by Carrier", "blue", "soft"],
  ["Partially Manifested", "blue", "soft"],
  ["Partially Departed", "blue", "soft"],
  ["Partially Transshipment", "blue", "soft"],
  ["Partially Dispatched", "blue", "soft"],
  ["Loaded at Loading Port", "blue", "solid"],
  ["Departed Loading Port", "blue", "solid"],
  ["Accepted by Carrier", "blue", "solid"],
  ["Manifested", "blue", "solid"],
  ["Departed", "blue", "solid"],
  ["Transshipment", "blue", "solid"],
  ["Fully Dispatched", "blue", "solid"],
  ["In Transit", "blue", "solid"],
  ["Dispatched", "blue", "solid"],
  ["Ready for Dispatch", "blue", "solid"],
  // cyan
  ["Partially Arrived at Discharge Port", "cyan", "soft"],
  ["Partially Discharged", "cyan", "soft"],
  ["Partially Gate Out at Discharge Port", "cyan", "soft"],
  ["Partially Arrived", "cyan", "soft"],
  ["Partially Received from Flight", "cyan", "soft"],
  ["Partially Released", "cyan", "soft"],
  ["Partially Final Destination", "cyan", "soft"],
  ["Arrived at Discharge Port", "cyan", "solid"],
  ["Discharged", "cyan", "solid"],
  ["Gate Out at Discharge Port", "cyan", "solid"],
  ["Arrived", "cyan", "solid"],
  ["Received from Flight", "cyan", "solid"],
  ["Released", "cyan", "solid"],
  ["Final Destination", "cyan", "solid"],
  // teal
  ["Partially ASN Created", "teal", "soft"],
  ["Partially ASN Validated", "teal", "soft"],
  ["ASN Created", "teal", "solid"],
  ["ASN Validated", "teal", "solid"],
  // green
  ["Partial Receipt Created", "green", "soft"],
  ["Partial Receipt Approved", "green", "soft"],
  ["Partially Received", "green", "soft"],
  ["Partially Receipted", "green", "soft"],
  ["Full Receipt Created", "green", "solid"],
  ["Full Receipt Approved", "green", "solid"],
  ["Fully Received", "green", "solid"],
  ["Fully Receipted", "green", "solid"],
  ["Delivered", "green", "solid"],
  // emerald
  ["Partial Invoice Created", "emerald", "soft"],
  ["Partial Invoice Approved", "emerald", "soft"],
  ["Full Invoice Created", "emerald", "solid"],
  ["Full Invoice Approved", "emerald", "solid"],
  ["Invoiced Approved", "emerald", "solid"],
  ["Invoiced", "emerald", "solid"],
  // forest
  ["Paid", "forest", "forest"],
  ["Completed", "forest", "forest"],
  // rose
  ["Delayed", "rose", "soft"],
  ["Customs Hold", "rose", "soft"],
  ["On Hold", "rose", "soft"],
  // red
  ["Changes Rejected", "red", "solid"],
  ["Cancelled", "red", "solid"],
]);

const JOURNEY_STATUS_MAP = new Map(
  JOURNEY_STATUS_ENTRIES.map(([label, family, tone]) => [label.toLowerCase(), { family, tone }]),
);

Object.freeze(JOURNEY_STATUS_MAP);

/**
 * @param {string} status
 * @returns {JourneyStatusTone | null}
 */
export function getJourneyStatusTone(status) {
  const normalized = String(status ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) {
    return null;
  }
  return JOURNEY_STATUS_MAP.get(normalized) ?? null;
}

/**
 * @param {string} status
 * @returns {{ text: string, bg: string, dot: string } | null}
 */
export function resolveJourneyColors(status) {
  const mapping = getJourneyStatusTone(status);
  if (!mapping) {
    return null;
  }
  const palette = JOURNEY_PALETTE[mapping.family];
  if (!palette) {
    return null;
  }
  if (mapping.tone === "forest") {
    return {
      text: palette.text,
      bg: palette.bgSolid,
      dot: palette.dotSolid,
    };
  }
  if (mapping.tone === "solid" || mapping.tone === "redSolid") {
    return {
      text: palette.text,
      bg: palette.bgSolid,
      dot: palette.dotSolid,
    };
  }
  return {
    text: palette.text,
    bg: palette.bgSoft,
    dot: palette.dotSoft,
  };
}

/** All journey status labels (for tests). */
export const JOURNEY_STATUS_LABELS = Object.freeze(JOURNEY_STATUS_ENTRIES.map(([label]) => label));
